import { jsPDF } from "jspdf";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { Card, CircularProgress, Grid, Icon, MenuItem, Select } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import { gql, useLazyQuery } from "@apollo/client";
import LogoSL from "assets/images/logo-sl.png";
// import LogoUPRO from "assets/images/logo-upro.png";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import boxShadows from "assets/theme/base/boxShadows";
import EditableText from "components/EditableText";
import PdfHeader from "../components/pdfHeader";
import { formatMoneyPunto } from "utils/formatters";
import { formatDate } from "utils/formatters";
import { nombreCompraDetalle } from "utils/presentacionCompra";

const textosIniciales = [
  "-", // 0 Contratación
  "-", // 1 Tipo
  "-", // 2 Oferta
  "-", // 3 Actividad
  "-",  // 4 "Nombre",
  " ", // 5 "Tipo",
];

const tipoDocumentos = [
  { id: 80, label: "CUIT" },
  { id: 86, label: "CUIL" },
  { id: 96, label: "DNI" },
]

const CotizacionProveedor = () => {
  const documentsRef = useRef(null);
  const { id } = useParams();
  const { md, lg } = boxShadows;

  const [textArray, setTextArray] = useState(textosIniciales);
  const [editable, setEditable] = useState(true);

  const [selected, setSelected] = useState(null);

  const [data, setData] = useState(null);
  const [getPedido, { loading }] = useLazyQuery(
    gql`
      query getPedidoCotizacionProveedor($id: ID) {
        pedidosCompra(filter: { id: $id }) {
          id
          numeroExpediente
          fechaSolicitud
          licitacionInicio
          licitacionFin
          cotizaciones{
            id
            createdAt
            proveedor{
              id
              razonSocial
              nombre
              apellido
              tipoDocumento
              documento
            }
            detalles{
              id
              requierePack
              nombreCompra
              unidad
              pedidoDetalle{
                nombreCompra
                nombreServicio
                materiaPrima{
                  id
                  nombre
                  tipoUnidad {
                    nombre
                  }
                }
                cantidadVerificada
              }
              nombreAlternativo
              precio
              observacion
            }
            cotizacionObservacion
            createdAt
            updatedAt
          }
        }
      }
    `,
    {
      onCompleted: (data) => {
        if (!data?.pedidosCompra[0]) return;
        setData(data.pedidosCompra[0]);
      },
    }
  );

  useEffect(() => {
    if (id) {
      getPedido({ variables: { id } });
    }
  }, [id]);

  const handleGeneratePdf = async () => {
    if (!selected || !selected.proveedor) {
      console.error("Proveedor no seleccionado o datos incompletos");
      return;
    }
    setEditable(false);
    const document = documentsRef.current.childNodes[0];
    const doc = new jsPDF({
      format: "a4",
      unit: "px",
      orientation: "landscape",
      hotfixes: ["px_scaling"],
      compress: true,
    });

    await doc.html(document, {
      margin: [40, 0, 40, 0],
      callback(doc) {
        doc.save(`exd-pedidoNro-${data.id}-cotizacion-proveedor-${selected.proveedor.id}.pdf`);
      },
    });
    setEditable(true);
  };

  const handleTextChange = (e, index) => {
    const newTextArray = [...textArray];
    newTextArray[index] = e.target.innerText;
    setTextArray(newTextArray);
  };

  const licitacionFinalizada = data?.licitacionFin && new Date(data.licitacionFin).getTime() <= Date.now();
  if (!data) return (
    <EmpleadoLayout>
      <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={3}>
        {loading ? (<>
          <CircularProgress size={40} color="primary" />
          <SoftTypography variant="h5">Cargando</SoftTypography>
        </>) : (<>
          <Icon fontSize="large">search_off</Icon>
          <SoftTypography variant="h5">No se encontró el pedido</SoftTypography>
        </>)}
      </SoftBox>
    </EmpleadoLayout>
  );

  if (!licitacionFinalizada) {
    return (
      <EmpleadoLayout>
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={3}>
          <Icon fontSize="large" color="warning">lock</Icon>
          <SoftTypography variant="h5" color="warning">
            Documento disponible cuando finalice el período de presentación de ofertas.
          </SoftTypography>
        </SoftBox>
      </EmpleadoLayout>
    );
  }

  return (
    <EmpleadoLayout>
      <SoftBox
        p={2}
        display="flex"
        alignItems="center"
        flexDirection="column"
        minHeight="100vh"
        height="100%"
        gap={4}
      >
        <SoftBox
          sx={{
            position: "relative",
            overflow: "hidden",
            width: "1123px",
            backgroundColor: "#ffffff",
            boxShadow: md,
            py: 2,
            px: 6,
          }}
        >
          <Grid container>
            <Grid item xs={4}>
              <SoftBox>
                <SoftTypography variant="caption">Proveedor</SoftTypography>
                <Select
                  value={selected?.id || "-1"}
                  onChange={(e) => {
                    setSelected(data.cotizaciones.find((cotizacion) => cotizacion.id === e.target.value));
                  }}
                  disabled={!data.licitacionFin || new Date(data.licitacionFin).getTime() > Date.now()}
                >
                  <MenuItem disabled value="-1">Seleccionar proveedor</MenuItem>
                  {data.cotizaciones.map((cotizacion) => (
                    <MenuItem key={cotizacion.id} value={cotizacion.id}>
                      {[
                        cotizacion.proveedor.razonSocial,
                        cotizacion.proveedor.nombre,
                        cotizacion.proveedor.apellido,
                      ].join(" ")}
                    </MenuItem>
                  ))}
                </Select>
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>

        <div ref={documentsRef}>
          <SoftBox
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "1123px",
              backgroundColor: "#ffffff",
              boxShadow: lg,
              px: 6,
            }}
            pb={5}
          >
            {/* ACA VA TODO EL CODIGO */}
            <PdfHeader />

            <SoftBox display="flex" flexDirection="column" gap={2} lineHeight={2} fontSize="12px">
              <SoftBox textAlign="center">
                <SoftTypography variant="h6">COTIZACIÓN DE PROVEEDOR</SoftTypography>
              </SoftBox>
              <Grid
                container
                border="1pt solid black"
                sx={{ '& > *:not(:last-child)': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
                  Datos de la Oferta
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}
                >
                  <Grid item xs={2} px={1}>Solicitud de Gasto</Grid>
                  <Grid item xs={2} px={1}>{data?.id || "-"}</Grid>
                  <Grid item xs={2} px={1}>Fecha Apertura</Grid>
                  <Grid item xs={2} px={1}>
                    {data?.licitacionInicio ? formatDate(data.licitacionInicio, "dd/MM/yyyy") : "-"}
                  </Grid>
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}
                >
                  <Grid item xs={2} px={1}>Contratación</Grid>
                  <Grid item xs={2} px={1}>
                    <EditableText
                      tooltip="Contratación"
                      editable={editable}
                      content={textArray[0]}
                      handleOnBlur={(e) => handleTextChange(e, 0)}
                    />
                  </Grid>
                  <Grid item xs={2} px={1}>Tipo</Grid>
                  <Grid item xs={2} px={1}>
                    <EditableText
                      tooltip="Tipo"
                      editable={editable}
                      content={textArray[1]}
                      handleOnBlur={(e) => handleTextChange(e, 1)}
                    />
                  </Grid>
                  <Grid item xs={2} px={1}>Fecha Cierre</Grid>
                  <Grid item xs={2} px={1}>
                    {data?.licitacionFin ? formatDate(data.licitacionFin, "dd/MM/yyyy") : "-"}
                  </Grid>
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}>
                  <Grid item xs={2} px={1}>Oferta</Grid>
                  <Grid item xs={6} px={1}>
                    <EditableText
                      tooltip="Oferta"
                      editable={editable}
                      content={textArray[2]}
                      handleOnBlur={(e) => handleTextChange(e, 2)}
                    />
                  </Grid>
                  <Grid item xs={2} px={1}>Fecha Oferta</Grid>
                  <Grid item xs={2} px={1}>
                    {selected?.createdAt ? formatDate(selected.createdAt, "dd/MM/yyyy") : "-"}
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                container
                border="1pt solid black"
                sx={{ '& > *:not(:nth-last-child(-n+2))': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1}>Proveedor</Grid>

                <Grid item xs={6} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
                  {selected?.proveedor?.razonSocial ? "Razón Social" : "Apellido y Nombre"}
                </Grid>
                <Grid item xs={6} px={1}>{
                  selected?.proveedor?.razonSocial || `${selected?.proveedor?.apellido || ''} ${selected?.proveedor?.nombre || ''}`
                }</Grid>

                <Grid item xs={6} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>{
                  tipoDocumentos.find(e => e.id == selected?.proveedor?.tipoDocumento)?.label || 'Documento'}</Grid>
                <Grid item xs={6} px={1}>{selected?.proveedor?.documento}</Grid>

                <Grid item xs={6} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>Descripción de la Actividad</Grid>
                <Grid item xs={6} px={1}>
                  <EditableText
                    tooltip="Actividad"
                    editable={editable}
                    content={textArray[3]}
                    handleOnBlur={(e) => handleTextChange(e, 3)}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                border="1pt solid black"
                sx={{ '& > *:not(:last-child)': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1}>Bienes y Servicios Solicitados</Grid>

                <Grid
                  item
                  container
                  xs={12}
                  backgroundColor="#f0f0f0"
                  fontWeight="bold"
                  sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}
                >
                  <Grid item xs={4} px={1}>Descripción</Grid>
                  <Grid item xs={1} px={1}>Cantidad</Grid>
                  <Grid item xs={1} px={1}>Unidad</Grid>
                  <Grid item xs={1.5} px={1}>Precio U.</Grid>
                  <Grid item xs={1.5} px={1}>Precio Total</Grid>
                  <Grid item xs={2} px={1}>Observaciones</Grid>
                </Grid>
                {selected?.detalles.map((detalle, index) => (
                  <Grid
                    key={index}
                    item
                    container
                    xs={12}
                    sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}
                    lineHeight={1.5}
                  >
                    <Grid item xs={4} px={1}>
                      {nombreCompraDetalle(detalle.pedidoDetalle)}
                      {detalle.nombreAlternativo ? " - " + detalle.nombreAlternativo : ""}
                    </Grid>
                    <Grid item xs={1} px={1} textAlign="center">
                      {detalle.pedidoDetalle.cantidadVerificada}
                    </Grid>
                    <Grid item xs={1} px={1} textAlign="center">
                      {detalle?.requierePack === true ? detalle?.unidad : detalle.pedidoDetalle.materiaPrima?.tipoUnidad?.nombre || "Sin unidad"}
                    </Grid>
                    <Grid item xs={1.5} px={1} textAlign="right">
                      $ {formatMoneyPunto(detalle.precio)}
                    </Grid>
                    <Grid item xs={1.5} px={1} textAlign="right">
                      $ {formatMoneyPunto(detalle.precio * detalle.pedidoDetalle.cantidadVerificada)}
                    </Grid>
                    <Grid item xs={2} px={1}>
                      {detalle.observacion || ""}
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Grid
                container
                border="1pt solid black"
                sx={{ '& > *:not(:last-child)': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1}>Archivos Adjuntos</Grid>

                <Grid
                  item
                  container
                  xs={12}
                  backgroundColor="#f0f0f0"
                  fontWeight="bold"
                  sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}
                >
                  <Grid item xs={6} px={1}>Nombre</Grid>
                  <Grid item xs={6} px={1}>Tipo</Grid>
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}
                >
                  <Grid item xs={6} px={1}>
                    <EditableText
                      tooltip="Nombre"
                      editable={editable}
                      content={textArray[4]}
                      handleOnBlur={(e) => handleTextChange(e, 4)}
                    />
                  </Grid>
                  <Grid item xs={6} px={1}>
                    <EditableText
                      tooltip="Tipo"
                      editable={editable}
                      content={textArray[5]}
                      handleOnBlur={(e) => handleTextChange(e, 5)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </SoftBox>
          </SoftBox>
        </div>
        <SoftBox
          hidden={!editable}
          variant="contained"
          bgColor="white"
          p={2}
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 1000,
            boxShadow:
              "inset 0rem 0rem 0.0625rem 0.0625rem rgb(255 255 255 / 90%), 0rem 1.25rem 1.6875rem 0rem rgb(0 0 0 / 5%)",
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: `saturate(200%) blur(${pxToRem(30)})`,
          }}
        >
          <Grid container spacing={2} justifyContent="flex-end" id="button">
            <Grid item>
              <SoftButton
                variant="gradient"
                color="error"
                onClick={handleGeneratePdf}
                disabled={!selected || !selected.proveedor}
              >
                <Icon fontWeight="bold">picture_as_pdf</Icon>
                &nbsp;&nbsp;Descargar PDF
              </SoftButton>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </EmpleadoLayout>
  );
};

export default CotizacionProveedor;
